import { currentLocale } from './locale';
/**
 * currency.js — devise du tenant (ISO 4217).
 * La devise est choisie par le tenant (tenants.currency, défaut XAF) dans
 * Paramètres > Organisation, et utilisée pour tous les affichages monétaires.
 * PUR (testable).
 */

/** Devises proposées dans la liste déroulante (obligatoires + usuelles). */
export const CURRENCIES = [
  { code: 'XAF', label: 'Franc CFA (CEMAC) — XAF', symbol: 'FCFA' },
  { code: 'XOF', label: 'Franc CFA (UEMOA) — XOF', symbol: 'FCFA' },
  { code: 'USD', label: 'Dollar américain — USD', symbol: '$' },
  { code: 'CAD', label: 'Dollar canadien — CAD', symbol: '$ CA' },
  { code: 'EUR', label: 'Euro — EUR', symbol: '€' },
  { code: 'GBP', label: 'Livre sterling — GBP', symbol: '£' },
  { code: 'NGN', label: 'Naira nigérian — NGN', symbol: '₦' },
  { code: 'GHS', label: 'Cedi ghanéen — GHS', symbol: 'GH₵' },
  { code: 'MAD', label: 'Dirham marocain — MAD', symbol: 'DH' },
  { code: 'TND', label: 'Dinar tunisien — TND', symbol: 'DT' },
  { code: 'DZD', label: 'Dinar algérien — DZD', symbol: 'DA' },
  { code: 'EGP', label: 'Livre égyptienne — EGP', symbol: 'E£' },
  { code: 'KES', label: 'Shilling kényan — KES', symbol: 'KSh' },
  { code: 'ZAR', label: 'Rand sud-africain — ZAR', symbol: 'R' },
  { code: 'CDF', label: 'Franc congolais — CDF', symbol: 'FC' },
  { code: 'GNF', label: 'Franc guinéen — GNF', symbol: 'FG' },
  { code: 'RWF', label: 'Franc rwandais — RWF', symbol: 'FRw' },
  { code: 'CHF', label: 'Franc suisse — CHF', symbol: 'CHF' },
  { code: 'CNY', label: 'Yuan chinois — CNY', symbol: '¥' },
  { code: 'AED', label: 'Dirham émirati — AED', symbol: 'AED' },
];

/**
 * Les codes de la liste des devises du produit (ZAR comprise), suivis des codes demandés qui n'y
 * figurent pas — typiquement la valeur déjà enregistrée sur une fiche, pour qu'une liste déroulante
 * ne la perde jamais en silence. Remplace les listes de devises recopiées dans les écrans.
 */
export const codesDevises = (...supplementaires) => {
  const codes = CURRENCIES.map((c) => c.code);
  return [...codes, ...supplementaires.filter((c) => c && !codes.includes(c))];
};

/**
 * Le symbole d'une devise de la liste du produit (« FCFA » pour XAF, « R » pour ZAR), à défaut son code.
 * Sans argument : la devise du tenant actif. Remplace le suffixe « FCFA » écrit dans les libellés.
 */
export const symboleDevise = (code = getActiveCurrency()) => CURRENCIES.find((c) => c.code === code)?.symbol || code;

export const DEFAULT_CURRENCY = 'XAF';

/** Devises sans décimales usuelles (Intl gère déjà XAF/XOF, gardé en garde-fou). */
const ZERO_DECIMAL = new Set(['XAF', 'XOF', 'GNF', 'RWF', 'CDF']);

export function isValidCurrency(code) {
  return typeof code === 'string' && /^[A-Z]{3}$/.test(code);
}

export function resolveTenantCurrency(tenant) {
  const code = tenant?.currency;
  return isValidCurrency(code) ? code : DEFAULT_CURRENCY;
}

/**
 * Formate un montant dans la devise donnée (fr-FR par défaut).
 * formatMoney(150000, 'XAF') → « 150 000 FCFA » ; formatMoney(99.5, 'EUR') → « 99,50 € »
 */
// Devise du tenant actif, synchronisée par TenantContext à chaque changement de
// contexte. Permet aux vues d'appeler fmtMoney(x) sans transporter la devise.
let activeCurrency = DEFAULT_CURRENCY;
export function setActiveCurrency(code) {
  activeCurrency = isValidCurrency(code) ? code : DEFAULT_CURRENCY;
}
export function getActiveCurrency() {
  return activeCurrency;
}
export function fmtMoney(amount, locale) {
  return formatMoney(amount, activeCurrency, locale);
}

export function formatMoney(amount, currency = DEFAULT_CURRENCY, locale = currentLocale()) {
  const value = Number(amount);
  if (!Number.isFinite(value)) return '—';
  const code = isValidCurrency(currency) ? currency : DEFAULT_CURRENCY;
  try {
    const formatted = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: ZERO_DECIMAL.has(code) ? 0 : undefined,
    }).format(value);
    // Intl affiche « FCFA » comme « F CFA »/« FCFA » selon les moteurs ; on unifie.
    return code === 'XAF' || code === 'XOF' ? formatted.replace(/F\s?CFA/i, 'FCFA') : formatted;
  } catch {
    return `${value.toLocaleString(locale)} ${code}`;
  }
}
